import { NetworkStatus } from '@apollo/client';
import { useMemo } from 'react';

import { useAddresses } from 'src/graphql/entities/address';
import { ConditionInput, SortingInput } from 'src/graphql/types';

import useDebouncedValue from 'src/utils/hooks/use-debounced-value';

const DEFAULT_FILTER: ConditionInput[] = [];

const DEFAULT_TARGET_IDS = [
  'client/addresses/addressId',
  'clientPhoneNumber/client/addresses/addressId',
  'clientEmail/client/addresses/addressId',
  'address/addressId',
];

export const useSearchAddresses = (params: {
  searchTerm: string | null;
  targetIdAttributes?: string[];
  filter?: ConditionInput[];
  limit?: number;
  sort?: SortingInput | SortingInput[];
  skip?: boolean;
  showResultsWhenEmptySearchTerm?: boolean;
  dynamicAttributePaths?: string[];
  includeDeleted?: boolean;
  context?: { omitIsolationModeHeader?: boolean };
}) => {
  const searchTerm = useDebouncedValue(params.searchTerm, 500);

  const {
    filter = DEFAULT_FILTER,
    skip,
    sort,
    showResultsWhenEmptySearchTerm,
    targetIdAttributes,
    limit,
    dynamicAttributePaths,
    includeDeleted,
    context,
  } = params;

  const queryOptions = useMemo(() => {
    const searchParams = searchTerm
      ? {
          searchTerm,
          targetIdAttributes: targetIdAttributes ?? DEFAULT_TARGET_IDS,
        }
      : undefined;

    return {
      filters: filter,
      sort,
      limit,
      offset: 0,
      searchParams,
      dynamicAttributePaths,
      includeDeleted,
    };
  }, [
    targetIdAttributes,
    filter,
    searchTerm,
    sort,
    limit,
    dynamicAttributePaths,
    includeDeleted,
  ]);

  const { addresses, loading, networkStatus, ...rest } = useAddresses(queryOptions, {
    skip: skip || (!showResultsWhenEmptySearchTerm && !searchTerm),
    context,
  });

  return {
    ...rest,
    isLoading: loading,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    records: addresses,
  };
};
